import {
  Container,
  Divider,
  Typography,
  Stack,
  Box,
  Breadcrumbs,
  Link,
  Alert,
} from '@mui/material'
import React, {
  useState,
  useEffect,
} from 'react'

import { Link as RouterLink } from 'react-router-dom'

import { lang } from '@common-sense-privacy/common'

import {
  useGetPlansQuery,
  useGetSubscriptionQuery,
  usePostCreateSubscriptionMutation,
  useCreateSetupIntentMutation,
} from '@/services/stripe'

import { useAlert } from '@/context/AlertContext'
import useNavigate from '@/hooks/useNavigate'
import useWaitForSubscriptionChange from '@/hooks/useWaitForSubscriptionChange'
import useRefreshAccessToken from '@/hooks/useRefreshAccessToken'

import BackLink from '@/components/BackLink'
import PlanCards from '@/components/PlanCards'
import Loading from '@/screens/Wizard/components/Loading'

import { isHigherTierPlan } from '@/utils/stripe'

import dateTime from '@/utils/dateTime'

import {
  Product,
  Props,
} from './types'

function Plans({
  subtitle,
  requiresPaidPlan,
  onSelectFreePlan,
  backLink,
  editMode,
  onSuccessLink,
  finalize,
}: Props): React.ReactElement {
  const [
    waitForSubscription,
    setWaitForSubscription,
  ] = useState(false)

  const [
    downgradePlan,
    setDowngradePlan,
  ] = useState<{
    currentProduct: Product | undefined,
    currentPlanExpiresIn: string | undefined,
    nextProduct: Product | undefined,
    nextPlanId: string,
    nextPlanPriceId: string,
  } | undefined>()

  const {
    data: plansResponse,
    isLoading: isLoadingPlans,
  } = useGetPlansQuery(undefined, { refetchOnMountOrArgChange: true })
  const {
    data: subscriptionResponse,
    isFetching: isFetchingSubscription,
  } = useGetSubscriptionQuery(undefined, { refetchOnMountOrArgChange: true })
  const [ postCreateSubscription ] = usePostCreateSubscriptionMutation()
  const [ createSetupIntent ] = useCreateSetupIntentMutation()

  const navigate = useNavigate()
  const { setAlert } = useAlert()

  const plans = plansResponse?.data || []
  const subscription = subscriptionResponse?.data

  const subscriptionChanged = useWaitForSubscriptionChange({
    canWait: waitForSubscription,
    subscription,
  })

  const hasMultipleProducts = subscription?.items?.data?.length ? subscription.items.data.length > 1 : false
  const accessTokenRefreshed = useRefreshAccessToken(subscriptionChanged)

  const submitPlan = (priceId: string, isSelectedPlanHigherTier: boolean) => {
    if (!subscription) return
    if (hasMultipleProducts) return

    postCreateSubscription({
      body: {
        priceId,
        finalize,
      },
    }).unwrap().then(() => {
      if (!isSelectedPlanHigherTier) {
        setAlert({
          type: 'success',
          description: `Success! Your plan has been changed. You will maintain access to paid features until ${downgradePlan?.currentPlanExpiresIn}`,
        })
      }
      setWaitForSubscription(true)
    }).catch(() => {
      setAlert({
        description: lang().messages.somethingWentWrong(),
        type: 'error',
      })
    })
  }

  const handlePlanSelected = (planId: string, priceId: string) => {
    if (!subscription) return
    if (hasMultipleProducts) return

    const selectedPlan = plans.find(plan => plan.id === planId)
    const currentPlan = plans.find(plan => plan.id === (subscription?.items.data[0].price.product as Product)?.id)

    if (!selectedPlan || !currentPlan) return

    const selectedPlanType = selectedPlan.metadata.type
    const currentPlanType = currentPlan.metadata.type
    const isSelectedPlanHigherTier = isHigherTierPlan(selectedPlanType, currentPlanType)

    if (isSelectedPlanHigherTier) {
      createSetupIntent({ body: { stripeCustomerId: subscription?.customer.id as string } })
        .unwrap()
        .then(response => {
          const paymentLink = editMode ? '/account/edit-plan/payment' : `/subscriptions/payment?finalize=${finalize}`

          navigate(paymentLink, {
            state: {
              planId,
              priceId,
              clientSecret: response?.data?.clientSecret,
              editMode,
              nextPage: onSuccessLink,
            },
          })
        })
        .catch(() => {
          setAlert({
            description: lang().messages.unknownError(),
            type: 'error',
          })
        })
    }
    else if (!isSelectedPlanHigherTier) {
      setDowngradePlan({
        currentProduct: currentPlan,
        nextProduct: selectedPlan,
        nextPlanId: planId,
        nextPlanPriceId: priceId,
        currentPlanExpiresIn: dateTime.format(
          new Date((subscription.items.data[0].current_period_end) * 1000),
          'MM.dd.yyyy',
        ),
      })
    }
    else {
      submitPlan(priceId, isSelectedPlanHigherTier)
    }
  }

  useEffect(() => {
    if (!accessTokenRefreshed) return

    if (onSelectFreePlan) {
      onSelectFreePlan()
    }
    else {
      navigate('/wizard')
    }
  }, [
    accessTokenRefreshed,
    onSelectFreePlan,
    navigate,
  ])

  return (
    <>
      <Container>
        {backLink && <Box mb={4}><BackLink to={backLink} /></Box>}
        {editMode && (
          <Box mb={4}>
            <Breadcrumbs>
              <Link
                href='/account'
                variant='body2'
                sx={{ color: 'text.primary' }}
              >My Account
              </Link>
              <Typography variant='body2'>Edit Plan</Typography>
            </Breadcrumbs>
          </Box>
        )}
        <Stack mb={4}>
          <Typography variant='h1'>Compare Plans</Typography>
          <Typography variant='intro' mb={4}>{subtitle}</Typography>
          <Divider />
        </Stack>
      </Container>
      <Container>
        {isLoadingPlans || isFetchingSubscription ? <Loading /> : (
          <>
            <PlanCards
              plans={plans}
              subscription={subscription}
              hasMultipleProducts={hasMultipleProducts}
              onContinue={!requiresPaidPlan ? onSelectFreePlan : undefined}
              onSelect={handlePlanSelected}
              requiresPaidPlan={requiresPaidPlan}
            />
            <Box sx={{
              padding: 1,
              borderRadius: 25,
              mt: 3,
              width: 'fit-content',
            }}
            >
              <Typography noWrap={true}>
                Need to cancel your account?{' '}
                <Link component={RouterLink} to='/contact-support?topic=Cancellation' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                  Contact Support
                </Link>
              </Typography>
            </Box>
          </>
        )}
        {hasMultipleProducts && (
          <Alert severity='info' sx={{ mt: 2 }}>
            To make changes to your plan, please contact our sales team.
          </Alert>
        )}
      </Container>
    </>
  )
}

export default React.memo(Plans)
